import type { ExtensionUpdateDialogData } from "@/components/features/ClubImport/ExtensionUpdateDialog";
import { selectShouldWarnExtensionOutdated } from "@/redux/clubImport/clubImportSlice";
import { showDialog } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

/**
 * Guards an import that runs in place: warns first when the extension is behind
 * the latest release, and runs `proceed` only when the user hits Continue.
 *
 * Not for CTAs that route to the tutorial — that page warns on its own. This is
 * for the launch the user reaches without passing it.
 *
 * `proceed` runs straight away when the extension is current, and when no
 * version could be read — a silent extension isn't something to nag about.
 *
 * Returns what `proceed` returned when it ran inline, and undefined when the
 * dialog was raised and the work is deferred to Continue.
 */
export const useExtensionUpdateNotice = () => {
  const dispatch = useDispatch<AppDispatch>();
  const needsUpdate = useSelector(selectShouldWarnExtensionOutdated);

  return useCallback(
    <T>(proceed: () => T): T | undefined => {
      if (!needsUpdate) return proceed();

      const data: ExtensionUpdateDialogData = {
        onContinue: () => void proceed(),
      };
      // Deferred a microtask: a CTA inside a dialog closes its own dialog right
      // after calling this, and a same-tick showDialog would be the one closed.
      queueMicrotask(() =>
        dispatch(showDialog({ modalType: "extension-update-notice", data })),
      );
      return undefined;
    },
    [dispatch, needsUpdate],
  );
};
